import { message } from 'antd'
import { isNumber, partition } from 'lodash'
import moment from 'moment'
import React from 'react'

import { CustomerFlowStatus } from '@/modules/customer/customer.schema'
import { DevlopStatus } from '@/modules/devlop/devlop.schema'
import { ProductionFlowStatus } from '@/modules/production-project/production.schema'

import { ProjectWorkResourceInput } from './special-project-work-resource.container'

export enum ProjectTableColumnKey {
  Subproject = 'subproject',
  Department = 'department',
  Email = 'email',
  PredictWorkload = 'predictWorkload',
  ActualWorkload = 'actualWorkload',
  Period = 'period',
  WeekWorkload = 'weekWorkload',
  QuarterWorkload = 'quarterWorkload',
}

export const ProjectTableColumnKeyMap = {
  [ProjectTableColumnKey.Subproject]: '子项目/自定义名称',
  [ProjectTableColumnKey.Department]: '部门',
  [ProjectTableColumnKey.Email]: '人员',
  [ProjectTableColumnKey.PredictWorkload]: '预估人力投入',
  [ProjectTableColumnKey.ActualWorkload]: '实际人力投入',
  [ProjectTableColumnKey.Period]: '周期',
  [ProjectTableColumnKey.QuarterWorkload]: '资源季度分布',
  [ProjectTableColumnKey.WeekWorkload]: '周工时',
}

export function getCustomIsColumnRequired<T extends CustomerFlowStatus | ProductionFlowStatus | DevlopStatus>(
  key: ProjectTableColumnKey,
  status?: T
) {
  if (status === undefined) return false
  switch (status) {
    case CustomerFlowStatus.待研发审批:
    case CustomerFlowStatus.项目已驳回:
    case CustomerFlowStatus.研发未开始: {
      switch (key) {
        case ProjectTableColumnKey.Subproject:
        case ProjectTableColumnKey.Department:
        case ProjectTableColumnKey.Period: {
          return true
        }
        default: {
          return false
        }
      }
    }
    default: {
      if (key === ProjectTableColumnKey.WeekWorkload || key === ProjectTableColumnKey.PredictWorkload) {
        return false
      }
      return true
    }
  }
}

export function getIsProductionColumnRequired<T extends CustomerFlowStatus | ProductionFlowStatus | DevlopStatus>(
  key: ProjectTableColumnKey,
  status?: T
) {
  if (status === undefined) return false
  switch (status) {
    case ProductionFlowStatus.待立项申请:
    case ProductionFlowStatus.待立项审批:
    case ProductionFlowStatus.待指派TO:
    case ProductionFlowStatus.项目已驳回: {
      switch (key) {
        case ProjectTableColumnKey.Subproject:
        case ProjectTableColumnKey.Department:
        case ProjectTableColumnKey.Period: {
          return true
        }
        default: {
          return false
        }
      }
    }
    default: {
      if (key === ProjectTableColumnKey.WeekWorkload || key === ProjectTableColumnKey.PredictWorkload) {
        return false
      }
      return true
    }
  }
}

export function getIsDevelopmentColumnRequired<T extends CustomerFlowStatus | ProductionFlowStatus | DevlopStatus>(
  key: ProjectTableColumnKey,
  status?: T
) {
  if (status === undefined) return false
  switch (status) {
    case DevlopStatus.未开始: {
      switch (key) {
        case ProjectTableColumnKey.Subproject:
        case ProjectTableColumnKey.Department:
        case ProjectTableColumnKey.Period: {
          return true
        }
        default: {
          return false
        }
      }
    }
    default: {
      if (key === ProjectTableColumnKey.WeekWorkload || key === ProjectTableColumnKey.PredictWorkload) {
        return false
      }
      return true
    }
  }
}

export function validateFields<T extends CustomerFlowStatus | ProductionFlowStatus | DevlopStatus>(
  data: Partial<ProjectWorkResourceInput>,
  fieldValidationFn: <T extends CustomerFlowStatus | ProductionFlowStatus | DevlopStatus>(
    key: ProjectTableColumnKey,
    status?: T
  ) => boolean,
  status?: T
) {
  const keys = Object.keys(data) as (keyof ProjectWorkResourceInput)[]

  let invalidField: ProjectTableColumnKey | null = null

  for (const key of keys) {
    switch (key) {
      case 'subprojectSelfId':
      case 'subprojectName': {
        const shouldValidate = fieldValidationFn(ProjectTableColumnKey.Subproject, status)
        if (shouldValidate && !data.subprojectName && !data.subprojectSelfId && !data.subprojectWorkResourceSelfId) {
          invalidField = ProjectTableColumnKey.Subproject
        }
        break
      }
      case 'departmentId': {
        const shouldValidate = fieldValidationFn(ProjectTableColumnKey.Department, status)
        if (shouldValidate && (!data.departmentId || !data.departmentName)) {
          invalidField = ProjectTableColumnKey.Department
        }
        break
      }

      case 'email': {
        const shouldValidate = fieldValidationFn(ProjectTableColumnKey.Email, status)
        if (shouldValidate && !data.email) {
          invalidField = ProjectTableColumnKey.Email
        }
        break
      }
      // case 'predictWorkload': {
      //   const shouldValidate = fieldValidationFn(ProjectTableColumnKey.PredictWorkload, status)
      //   if (shouldValidate && !data.predictWorkload) {
      //     invalidField = ProjectTableColumnKey.PredictWorkload
      //   }
      //   break
      // }
      // case 'actualWorkload': {
      //   if (isClosingProject && !data.actualWorkload) {
      //     invalidField = ProjectTableColumnKey.ActualWorkload
      //   }
      //   break
      // }
      case 'startDate':
      case 'endDate': {
        if (!data.startDate || !data.endDate) {
          invalidField = ProjectTableColumnKey.Period
        }
        break
      }
      // case 'quarters': {
      //   const shouldValidate = fieldValidationFn(ProjectTableColumnKey.QuarterWorkload, status)
      //   if (
      //     shouldValidate &&
      //     !(data.quarters || []).some((q) => {
      //       if (!q.year || !q.quarter || !data.startDate || !data.endDate) {
      //         return false
      //       }
      //       const isInRange = moment()
      //         .year(Number(q.year))
      //         .quarter(Number(q.quarter))
      //         .startOf('quarter')
      //         .isBetween(moment(data.startDate), moment(data.endDate), 'day', '[]')
      //       const workloadNum = Number(q.workload)
      //       return isInRange && !isNaN(workloadNum) && isNumber(workloadNum)
      //     })
      //   ) {
      //     invalidField = ProjectTableColumnKey.QuarterWorkload
      //   }
      //   break
      // }

      case 'weeks': {
        if (
          data.weeks &&
          data.weeks.length > 0 &&
          !(data.weeks || []).some((w) => {
            if (!w.year || !w.week || !w.startDate || !w.endDate || !data.startDate || !data.endDate) {
              return false
            }

            const workloadNum = Number(w.workload)

            return !Number.isNaN(workloadNum) && isNumber(workloadNum)
          })
        ) {
          invalidField = ProjectTableColumnKey.WeekWorkload
        }
        break
      }
    }

    if (invalidField) {
      message.warn(
        <span>
          字段 <span style={{ fontWeight: 'bold' }}>{ProjectTableColumnKeyMap[invalidField]}</span> 是必填项，请完成填写
        </span>
      )
      return false
    }
  }
  return true
}

export const getAllKeys = (records: { email: string; key: string; children?: Record[] }[], res: string[]) => {
  for (const record of records) {
    res.push(record.key)
    getAllKeys(record.children || [], res)
  }
}

type Record = { key: string; children?: Record[]; isCreating?: boolean; parentKey?: string | null; email: string }
export const getRecordByKey = (key: string, records: Record[], res: Record[]) => {
  for (const record of records) {
    if (record.key === key) {
      res.push(record)
      return
    }
    if (record.children) {
      getRecordByKey(key, record.children, res)
    }
  }
}

export const getParentByKey = (key: string, records: Record[], res: Record[]) => {
  for (let i = 0; i < records.length; i++) {
    const children = records[i].children || []
    if (children.length > 0 && children.some((child) => child.key === key)) {
      res.push(records[i])
      return
    }
    getParentByKey(key, children, res)
  }
}

export const getCreatingRecordFromTree = (records: Record[], creatingRecords: Record[]) => {
  for (const record of records) {
    if (record.isCreating) {
      creatingRecords.push(record)
    }
    getCreatingRecordFromTree(record.children || [], creatingRecords)
  }
}

export const setCreatingRecordToSubTree = (records: Record[], creatingRecords: Record[]) => {
  if (creatingRecords.length === 0 || records.length === 0) {
    return
  }

  for (const record of records) {
    const [creatingItems, otherItems] = partition(creatingRecords, (r) => r.isCreating && r.parentKey === record.key)
    setCreatingRecordToSubTree(record.children || [], otherItems)

    if (creatingItems.length > 0) {
      record['children'] = record.children ? [...record.children, ...creatingItems] : creatingItems
    }
  }
}

export function getIsValidWeekToUpsert({ year, week }: { year: number; week: number }) {
  const MostAllowedDelayedWeek = 4
  const now = moment().subtract(MostAllowedDelayedWeek, 'week').startOf('week')
  const startDate = moment().isoWeekYear(year).isoWeek(week).startOf('week')
  const isValid = now.isBefore(startDate, 'day')
  return isValid
}
